import React, { useState } from "react";
import {
  Palette,
  X,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ChevronDown,
  ChevronUp,
  Smartphone,
  Tablet,
  Laptop,
  Monitor,
} from "lucide-react";
import type { BreakpointKey, PaddingValues, ResponsivePadding } from "../types";
import { BREAKPOINTS } from "../types";

interface CellStyleModalProps {
  isOpen: boolean;
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  textColor: string;
  textAlign: "left" | "center" | "right";
  verticalAlign: "top" | "middle" | "bottom";
  padding: ResponsivePadding;
  fontSize: string;
  fontWeight: string;
  onBackgroundColorChange: (value: string) => void;
  onBorderColorChange: (value: string) => void;
  onBorderWidthChange: (value: number) => void;
  onTextColorChange: (value: string) => void;
  onTextAlignChange: (value: "left" | "center" | "right") => void;
  onVerticalAlignChange: (value: "top" | "middle" | "bottom") => void;
  onPaddingChange: (value: ResponsivePadding) => void;
  onFontSizeChange: (value: string) => void;
  onFontWeightChange: (value: string) => void;
  onApply: () => void;
  onClose: () => void;
}

const quickColors = [
  "#ffffff",
  "#f3f4f6",
  "#fee2e2",
  "#fef3c7",
  "#d1fae5",
  "#dbeafe",
  "#e0e7ff",
  "#fce7f3",
  "#f5f5f4",
  "#374151",
];

const paddingOptions = [
  { value: "0", label: "0" },
  { value: "2", label: "2px" },
  { value: "4", label: "4px" },
  { value: "6", label: "6px" },
  { value: "8", label: "8px" },
  { value: "10", label: "10px" },
  { value: "12", label: "12px" },
  { value: "16", label: "16px" },
  { value: "20", label: "20px" },
  { value: "24", label: "24px" },
];

const fontSizeOptions = [
  { value: "", label: "Default" },
  { value: "10", label: "10px" },
  { value: "11", label: "11px" },
  { value: "12", label: "12px" },
  { value: "13", label: "13px" },
  { value: "14", label: "14px" },
  { value: "15", label: "15px" },
  { value: "16", label: "16px" },
  { value: "18", label: "18px" },
  { value: "20", label: "20px" },
  { value: "24", label: "24px" },
];

const fontWeightOptions = [
  { value: "", label: "Default" },
  { value: "300", label: "Light" },
  { value: "400", label: "Normal" },
  { value: "500", label: "Medium" },
  { value: "600", label: "Semi Bold" },
  { value: "700", label: "Bold" },
];

const defaultPadding: PaddingValues = { top: "8", right: "12", bottom: "8", left: "12" };

const breakpointIcons: Record<BreakpointKey, React.FC<{ className?: string }>> = {
  xs: Smartphone,
  sm: Tablet,
  md: Laptop,
  lg: Monitor,
  xl: Monitor,
};

export const CellStyleModal: React.FC<CellStyleModalProps> = ({
  isOpen,
  backgroundColor,
  borderColor,
  borderWidth,
  textColor,
  textAlign,
  verticalAlign,
  padding,
  fontSize,
  fontWeight,
  onBackgroundColorChange,
  onBorderColorChange,
  onBorderWidthChange,
  onTextColorChange,
  onTextAlignChange,
  onVerticalAlignChange,
  onPaddingChange,
  onFontSizeChange,
  onFontWeightChange,
  onApply,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<"basic" | "responsive">("basic");
  const [expandedBreakpoint, setExpandedBreakpoint] = useState<BreakpointKey | null>("xs");

  if (!isOpen) return null;

  const updatePaddingForBreakpoint = (
    breakpoint: BreakpointKey,
    field: keyof PaddingValues,
    value: string
  ) => {
    onPaddingChange({
      ...padding,
      [breakpoint]: {
        ...padding[breakpoint],
        [field]: value,
      },
    });
  };

  const copyPaddingToAll = (sourceBreakpoint: BreakpointKey) => {
    const sourcePadding = padding[sourceBreakpoint];
    const newPadding: ResponsivePadding = {
      xs: { ...sourcePadding },
      sm: { ...sourcePadding },
      md: { ...sourcePadding },
      lg: { ...sourcePadding },
      xl: { ...sourcePadding },
    };
    onPaddingChange(newPadding);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Cell Styling
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          Apply styles to the selected cell(s). Select multiple cells by clicking and dragging.
          Mobile (xs) covers 340px-1079px.
        </p>

        {/* Tab Navigation */}
        <div className="flex border-b mb-4">
          <button
            type="button"
            onClick={() => setActiveTab("basic")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "basic"
                ? "border-primary-main text-primary-main"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Basic Styles
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("responsive")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "responsive"
                ? "border-primary-main text-primary-main"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Responsive Padding
          </button>
        </div>

        {activeTab === "basic" && (
          <div className="space-y-4">
            {/* Colors Row */}
            <div className="grid grid-cols-2 gap-4">
              {/* Background Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => onBackgroundColorChange(e.target.value)}
                    className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={backgroundColor}
                    onChange={(e) => onBackgroundColorChange(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="#ffffff"
                  />
                </div>
              </div>

              {/* Text Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={textColor || "#000000"}
                    onChange={(e) => onTextColorChange(e.target.value)}
                    className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={textColor}
                    onChange={(e) => onTextColorChange(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="#000000"
                  />
                </div>
              </div>
            </div>

            {/* Border Row */}
            <div className="grid grid-cols-2 gap-4">
              {/* Cell Border Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Border Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={borderColor}
                    onChange={(e) => onBorderColorChange(e.target.value)}
                    className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={borderColor}
                    onChange={(e) => onBorderColorChange(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="#000000"
                  />
                </div>
              </div>

              {/* Cell Border Width */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Border Width (px)</label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={borderWidth}
                  onChange={(e) => onBorderWidthChange(Math.max(0, Math.min(10, parseInt(e.target.value) || 0)))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-main/20 focus:border-primary-main"
                />
              </div>
            </div>

            {/* Text Alignment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Text Alignment</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onTextAlignChange("left")}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    textAlign === "left"
                      ? "bg-primary-main text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <AlignLeft className="w-4 h-4" />
                  Left
                </button>
                <button
                  type="button"
                  onClick={() => onTextAlignChange("center")}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    textAlign === "center"
                      ? "bg-primary-main text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <AlignCenter className="w-4 h-4" />
                  Center
                </button>
                <button
                  type="button"
                  onClick={() => onTextAlignChange("right")}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    textAlign === "right"
                      ? "bg-primary-main text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <AlignRight className="w-4 h-4" />
                  Right
                </button>
              </div>
            </div>

            {/* Vertical Alignment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Vertical Alignment</label>
              <div className="flex gap-2">
                {(["top", "middle", "bottom"] as const).map((align) => (
                  <button
                    key={align}
                    type="button"
                    onClick={() => onVerticalAlignChange(align)}
                    className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all capitalize ${
                      verticalAlign === align
                        ? "bg-primary-main text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {align}
                  </button>
                ))}
              </div>
            </div>

            {/* Font Size & Weight */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Size</label>
                <select
                  value={fontSize}
                  onChange={(e) => onFontSizeChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  {fontSizeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Weight</label>
                <select
                  value={fontWeight}
                  onChange={(e) => onFontWeightChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  {fontWeightOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Quick Colors */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Quick Background Colors</label>
              <div className="flex flex-wrap gap-2">
                {quickColors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => onBackgroundColorChange(color)}
                    className={`w-8 h-8 rounded border-2 ${
                      backgroundColor === color ? "border-primary-main" : "border-gray-300"
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "responsive" && (
          <div className="space-y-2">
            <p className="text-sm text-gray-500 mb-4">
              Set different padding values for each screen size breakpoint.
            </p>
            {(Object.keys(BREAKPOINTS) as BreakpointKey[]).map((key) => {
              const Icon = breakpointIcons[key];
              const isExpanded = expandedBreakpoint === key;
              const bp = BREAKPOINTS[key];

              return (
                <div key={key} className="border rounded-md overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpandedBreakpoint(isExpanded ? null : key)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      <span className="font-medium text-sm">{bp.label}</span>
                      <span className="text-xs text-gray-500">({bp.description})</span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="p-4 bg-white">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-600">Padding (px)</label>
                        <button
                          type="button"
                          onClick={() => copyPaddingToAll(key)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Copy to all breakpoints
                        </button>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        {(["top", "right", "bottom", "left"] as const).map((side) => (
                          <div key={side}>
                            <label className="block text-xs text-gray-500 mb-1 capitalize">
                              {side}
                            </label>
                            <select
                              value={padding[key]?.[side] || defaultPadding[side]}
                              onChange={(e) => updatePaddingForBreakpoint(key, side, e.target.value)}
                              className="w-full border border-gray-300 px-2 py-1 rounded text-sm bg-white"
                            >
                              {paddingOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Preview */}
        <div className="border-t pt-4 mt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Preview</h4>
          <div
            style={{
              backgroundColor: backgroundColor,
              color: textColor || "#000000",
              border: `${borderWidth}px solid ${borderColor}`,
              padding: padding.xs
                ? `${padding.xs.top}px ${padding.xs.right}px ${padding.xs.bottom}px ${padding.xs.left}px`
                : "12px",
              textAlign: textAlign,
              verticalAlign: verticalAlign,
              fontSize: fontSize ? `${fontSize}px` : undefined,
              fontWeight: fontWeight || undefined,
            }}
            className="text-sm rounded"
          >
            Cell preview content
          </div>
          <p className="text-xs text-gray-500 mt-1">Showing mobile (xs) padding preview</p>
        </div>

        <div className="flex gap-2 justify-end mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onApply}
            className="px-4 py-2 bg-primary-main text-white rounded-md text-sm font-medium hover:bg-primary-main/90 transition-all"
          >
            Apply Styles
          </button>
        </div>
      </div>
    </div>
  );
};


